// these utilities were copied from
// https://github.com/lifeomic/life-extend/blob/master/src/profile/countryUtils/countryUtils.ts
// and modified for use in phc
import { memoize } from 'lodash';
import { OfficialAndCommon, Country } from 'world-countries';

// CountryRecord extracted from
// https://github.com/lifeomic/life-extend/blob/master/src/lifeData/lifeData.types.ts
export interface CountryRecord {
  name: string;
  alpha3Code: string;
  alpha2Code: string;
}

const CountriesDataSource = {
  get CountriesJSON() {
    return require('world-countries');
  },
};

export const getCountryFlag = memoize((country?: string | null) => {
  if (!country) return '';

  const searchedCountry = CountriesDataSource.CountriesJSON.find(
    (obj: Country) => obj.cca3 === country || obj.cca2 === country,
  );
  if (!searchedCountry) return '';

  return searchedCountry.flag;
});

const TOP_COUNTRIES = ['USA', 'GBR', 'CAN', 'AUS', 'KEN', 'MEX'];

export const countryComparer = (a: CountryRecord, b: CountryRecord): number => {
  const topIndexA = TOP_COUNTRIES.indexOf(a.alpha3Code);
  const topIndexB = TOP_COUNTRIES.indexOf(b.alpha3Code);
  // Sort by order in the list if both are in there
  if (topIndexA !== -1 && topIndexB !== -1) {
    return topIndexA - topIndexB;
  }
  // If only one is in the lists then pick that one
  if (topIndexA !== -1) {
    return -1;
  }
  if (topIndexB !== -1) {
    return 1;
  }
  // Alphabetical sort
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
};

export const sortCountryData = (data: CountryRecord[]): CountryRecord[] => {
  // Return is just for convenience. Array sort mutates in place
  return data.sort(countryComparer);
};

const getPreferredLanguage = () => {
  return navigator && navigator.language
    ? navigator.language.slice(0, 2)
    : null;
};

export function getCountryNameLocale(
  preferredLanguage: string,
  countryTranslations: { [languageCode: string]: OfficialAndCommon },
  defaultName: string,
) {
  switch (preferredLanguage) {
    case 'es': {
      return countryTranslations?.spa?.common ?? defaultName;
    }
    case 'de': {
      return countryTranslations?.deu?.common ?? defaultName;
    }
    case 'fr': {
      return countryTranslations?.fra?.common ?? defaultName;
    }
    case 'pt': {
      return countryTranslations?.por?.common ?? defaultName;
    }
    case 'en':
    default: {
      return defaultName;
    }
  }
}

export const getCountriesWithCodes = memoize(() => {
  const preferredLanguage = getPreferredLanguage();
  const countryMap = CountriesDataSource.CountriesJSON.map(
    (country: Country) => ({
      name: getCountryNameLocale(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        preferredLanguage,
        country.translations,
        country.name.common,
      ),
      alpha2Code: country.cca2,
      alpha3Code: country.cca3,
    }),
  );

  return sortCountryData(countryMap);
});
